import { Component, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { BsModalService, ModalOptions } from 'ngx-bootstrap/modal';
import { IconName } from 'ngx-bootstrap-icons';
import { ToastrService } from 'ngx-toastr';
import { SubscriptionLike } from 'rxjs';

import { Dietitian } from '../../../../data/dietitian/domain/dietitian';
import { BundledFeaturesModel } from '../../../../data/dietitian/domain/subscription/subscription_commands';
import {
  StripePrice,
  StripeProduct,
} from '../../../../data/dietitian/repositories/subscription_repository';
import { IncomingMessageEventProvider } from '../../../../data/notification/incoming-message-event-provider';
import { MsdAiDiscoveryTourComponent } from '../../../components/msd-ai-discovery-tour/msd-ai-discovery-tour.component';
import {
  FeatureModel,
  SubscriptionModel,
} from '../account/routes/account-subscription/account-subscription.component';
import { MsdAiShowcaseFacade } from './msd-ai-showcase.facade';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'msd-msd-ai-showcase',
  templateUrl: './msd-ai-showcase.component.html',
  styleUrls: ['./msd-ai-showcase.component.scss'],
  providers: [MsdAiShowcaseFacade],
})
export class MsdAiShowcaseComponent implements OnDestroy {
  featureModel: FeatureModel | undefined;
  latestSubscription: SubscriptionModel | undefined;
  loadingSubscription = false;
  errorSubscription: string | undefined;
  bundledFeaturesModels: BundledFeaturesModel[];
  private dietitian: Dietitian | undefined;
  private dietitianSubscription: SubscriptionLike;
  private incomingMessageEventSubscription: SubscriptionLike;

  constructor(
    private facade: MsdAiShowcaseFacade,
    private toastrService: ToastrService,
    private modalService: BsModalService,
    private router: Router,
    incomingMessageEventProvider: IncomingMessageEventProvider,
  ) {
    this.incomingMessageEventSubscription =
      incomingMessageEventProvider.events$.subscribe((event) => {
        if (
          event &&
          event.entity.props &&
          // eslint-disable-next-line no-prototype-builtins
          event.entity.props.hasOwnProperty('subscriptionId') &&
          // eslint-disable-next-line no-prototype-builtins
          event.entity.props.hasOwnProperty('subscriptionStatus') &&
          // eslint-disable-next-line no-prototype-builtins
          event.entity.props.hasOwnProperty('productId') &&
          // eslint-disable-next-line no-prototype-builtins
          event.entity.props.hasOwnProperty('subscriptionType')
        ) {
          if (
            event.entity.props['subscriptionType'] === 'feature' &&
            event.entity.props['subscriptionStatus'] === 'active'
          ) {
            this.toastrService.info('Abonnement Option souscrit');
            location.reload();
          }
        }
      });

    this.dietitianSubscription = this.facade.dietitianState$.subscribe(
      (event) => {
        this.dietitian = event.entity;
        this.loadingSubscription = true;
        if (this.dietitian) {
          this.loadSubscription(this.dietitian).then(() => {
            if (!this.isAlreadySubscribed) {
              this.errorSubscription =
                "Vous devez disposer d'un abonnement Premium pour avoir accès à cette option";
            }
          });
          facade
            .getFeatureModel(this.dietitian, 'msd-ai')
            .then((featureModel) => {
              this.featureModel = featureModel;
              if (
                featureModel.subscription &&
                featureModel.subscription.status === 'active'
              ) {
                this.errorSubscription =
                  'Vous avez déjà souscrit à cette option...';
                this.loadingSubscription = false;
                this.router.navigate(['dashboard']).then();
              } else {
                facade
                  .findAllBundledFeatures(
                    this.featureModel.feature.id.toString(),
                  )
                  .then((bundledFeatures) => {
                    this.bundledFeaturesModels = bundledFeatures;
                  })
                  .finally(() => (this.loadingSubscription = false));
              }
            })
            .catch(() => {
              this.errorSubscription =
                "Une erreur s'est produite, veuillez essayer ultérieurement...";
              this.loadingSubscription = false;
            });
        }
      },
    );
  }

  ngOnDestroy(): void {
    this.dietitianSubscription?.unsubscribe();
    this.incomingMessageEventSubscription?.unsubscribe();
  }

  async loadSubscription(dietitian: Dietitian | undefined) {
    if (dietitian) {
      this.latestSubscription = await this.facade.geLatestSubscription(
        dietitian,
      );
    }
  }

  getIcon(icon: string | undefined): IconName {
    if (icon) {
      return icon as IconName;
    }
    return 'stars';
  }

  getPrice(stripeProduct: StripeProduct | undefined) {
    if (
      stripeProduct &&
      stripeProduct.prices &&
      stripeProduct.prices.length > 0
    ) {
      const defaultPrices = stripeProduct.prices.filter(
        (p) => p.id === stripeProduct.defaultPrice,
      );
      const defaultAmount = defaultPrices[0].amount;
      const amount = (Number(defaultAmount) / 100).toFixed(2);
      return (
        amount +
        (defaultPrices[0].currency === 'eur'
          ? '€'
          : defaultPrices[0].currency) +
        this.getPeriod(defaultPrices[0])
      );
    }
    return '';
  }

  getPriceCrossedOut(
    stripeProduct: StripeProduct | undefined,
  ): string | undefined {
    if (
      stripeProduct &&
      stripeProduct.prices &&
      stripeProduct.prices.length > 1
    ) {
      const defaultPrices = stripeProduct.prices.filter(
        (p) => p.id === stripeProduct.defaultPrice,
      );
      const defaultAmount = defaultPrices[0].amount;
      const higherPrices = stripeProduct.prices
        .filter((p) => p.amount > defaultAmount)
        .sort((a, b) => b.amount - a.amount);

      if (higherPrices && higherPrices.length > 0) {
        const amount = (Number(higherPrices[0].amount) / 100).toFixed(2);
        return (
          amount +
          (higherPrices[0].currency === 'eur'
            ? '€'
            : higherPrices[0].currency) +
          this.getPeriod(higherPrices[0])
        );
      }
    }
    return undefined;
  }

  getPeriod(price: StripePrice): string {
    switch (price.period) {
      case 'year_1':
        return ' / an';
      case 'month_1':
      default:
        return ' / mois';
    }
  }

  get isAlreadySubscribed(): boolean {
    return (
      !!this.latestSubscription &&
      !!this.latestSubscription.subscription &&
      this.latestSubscription.subscription.isActive
    );
  }

  goSubscription() {
    this.router.navigate(['dashboard', 'account', 'subscription']).then();
  }

  discoveryTour() {
    const initialState: ModalOptions = {
      class: 'modal-dialog-centered modal-xl',
    };
    this.modalService.show(MsdAiDiscoveryTourComponent, initialState);
  }
}
